import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { CancellablePromise } from '@atlassian/search-client';
import { AggregatorClient, responseErrorToError } from '../../common/clients';
import { ContentType, Scope, AttributeType } from './response-types';
import { SimpleCache } from '../../utils/simple-cache';
import { UnreachableError } from '../../utils/safety';
import { transformJiraIssueScope, transformJiraPlanScope, transformJiraBoardProjectFilterScope, transformJiraProjectScope, transformUrsPeople } from './jira-search-result-transformers';
import { limitToMaxSiteUsage } from '../../common/clients/multi-site-utils';
const RECENT_PLANS_SOFT_LIMIT = 2;
const NAVIGATION_V3_EXPERIENCE = 'jira.nav-v3';
export class JiraSearchClient {
  constructor(config) {
    _defineProperty(this, "abTestDataCache", null);
    _defineProperty(this, "permissionsDataCache", null);
    _defineProperty(this, "recentIssuesSupplier", async (context, sites) => {
      const results = this.searchForScope('', [], context, [Scope.JiraIssue], JiraSearchClient.ITEM_RESULT_LIMIT, true, sites)[Scope.JiraIssue];
      if (!results) {
        throw new Error(`Failed to retrieve recent results for scope ${Scope.JiraIssue}`);
      }
      return results.promise();
    });
    // For recent results, we only want to show up to 2 recent plans as the last 2 results
    // as we cannot accurately interleave  recent plans with recent boards, projects, filter results.
    // However, it is valid to show more than 2 plans in the case where other entity types have been exhausted.
    _defineProperty(this, "interleaveRecentBoardsProjectsFiltersPlans", (boardProjectFilterResults, planResults) => {
      const timings = planResults.timings > boardProjectFilterResults.timings ? planResults.timings : boardProjectFilterResults.timings;
      const {
        items
      } = boardProjectFilterResults;
      if (planResults.items.length >= RECENT_PLANS_SOFT_LIMIT) {
        while (items.length > JiraSearchClient.PROJECT_BOARD_FILTER_PLAN_LIMIT - RECENT_PLANS_SOFT_LIMIT) {
          items.pop();
        }
      } else {
        while (items.length > JiraSearchClient.PROJECT_BOARD_FILTER_PLAN_LIMIT - planResults.items.length) {
          items.pop();
        }
      }
      for (let planCount = 0; items.length < JiraSearchClient.PROJECT_BOARD_FILTER_PLAN_LIMIT && planResults.items.length > planCount; planCount++) {
        items.push(planResults.items[planCount]);
      }
      return {
        timings: timings,
        items: items,
        totalSize: items.length
      };
    });
    _defineProperty(this, "search", (query, filters, context, queryVersion, hasAdvancedRoadmapsAccess, sites) => {
      var _filters$;
      const issueSearch = this.searchForScope(query, filters, context, [Scope.JiraIssue], JiraSearchClient.ITEM_RESULT_LIMIT, false, sites, queryVersion);
      const onlyDateFilterEnabled = filters.length === 1 && ((_filters$ = filters[0]) === null || _filters$ === void 0 ? void 0 : _filters$.type) === 'dateFromLastModified';
      const scopes = hasAdvancedRoadmapsAccess ? [Scope.JiraBoardProjectFilterPlan] : [Scope.JiraBoardProjectFilter];
      const limit = hasAdvancedRoadmapsAccess ? JiraSearchClient.PROJECT_BOARD_FILTER_PLAN_LIMIT : JiraSearchClient.PROJECT_BOARD_FILTER_LIMIT;
      const jiraBoardProjectFilterSearch = filters.length === 0 || onlyDateFilterEnabled ? this.searchForScope(query, filters, context, scopes, limit, false, sites, queryVersion) : {};
      return {
        ...issueSearch,
        ...jiraBoardProjectFilterSearch
      };
    });
    _defineProperty(this, "searchForScope", (query, filters, context, scopes, resultLimit, isForRecentResults, sites = [], queryVersion) => {
      const modelParams = queryVersion || queryVersion === 0 ? [{
        '@type': 'queryParams',
        queryVersion
      }] : [];
      const allFilters = [];
      let baseProjectFilters = null;
      let baseAssigneeFilters = null;
      let baseBinaryStatusCategoryFilters = null;
      let lastModifiedFilter = {
        '@type': 'lastModified'
      };
      let lastViewedFilter = {
        '@type': 'lastViewed'
      };
      filters.forEach(filter => {
        switch (filter.type) {
          case 'project':
            if (!baseProjectFilters) {
              baseProjectFilters = {
                '@type': 'projects',
                projectIds: []
              };
              allFilters.push(baseProjectFilters);
            }
            baseProjectFilters.projectIds.push(filter.id);
            break;
          case 'assignee':
            if (!baseAssigneeFilters) {
              baseAssigneeFilters = {
                '@type': 'assignees',
                accountIds: []
              };
              allFilters.push(baseAssigneeFilters);
            }
            baseAssigneeFilters.accountIds.push(filter.id);
            break;
          case 'binary_status_category':
            if (!baseBinaryStatusCategoryFilters) {
              baseBinaryStatusCategoryFilters = {
                '@type': 'binary_status_categories',
                binaryStatusCategories: []
              };
              allFilters.push(baseBinaryStatusCategoryFilters);
            }
            baseBinaryStatusCategoryFilters.binaryStatusCategories.push(filter.id);
            break;
          case 'dateFromLastModified':
            filter.id ? lastModifiedFilter.from = filter.id : null;
            break;
          case 'dateFromLastViewed':
            filter.id ? lastViewedFilter.from = filter.id : null;
            break;
          case 'dateToLastModified':
            filter.id ? lastModifiedFilter.to = filter.id : null;
            break;
          default:
            {
              break;
            }
        }
      });
      if (lastModifiedFilter.from || lastModifiedFilter.to) {
        allFilters.push(lastModifiedFilter);
      }
      if (lastViewedFilter.from) {
        allFilters.push(lastViewedFilter);
      }
      const responses = this.aggregatorClient.search({
        query,
        context,
        scopes,
        modelParams,
        resultLimit,
        filters: allFilters,
        experience: NAVIGATION_V3_EXPERIENCE,
        sites: sites.length > 0 ? sites.map(s => s.cloudId) : undefined
      });
      return scopes.reduce((results, currentScope) => ({
        ...results,
        [currentScope]: CancellablePromise.from(responses.then(({
          response,
          requestDurationMs
        }) => {
          const scopedResponse = response.retrieveScope(currentScope);
          if (!scopedResponse) {
            throw new Error(`Expected a response but did not get any for scope: ${currentScope}`);
          }
          if (scopedResponse.error) {
            throw responseErrorToError(scopedResponse.error);
          }
          if (currentScope === Scope.JiraIssue) {
            return transformJiraIssueScope(scopedResponse, isForRecentResults, requestDurationMs);
          } else if (currentScope === Scope.JiraPlan) {
            return transformJiraPlanScope(scopedResponse.results, isForRecentResults, requestDurationMs);
          } else if (currentScope === Scope.JiraBoardProjectFilter) {
            return transformJiraBoardProjectFilterScope(scopedResponse.results, isForRecentResults, requestDurationMs);
          } else if (currentScope === Scope.JiraBoardProjectFilterPlan) {
            return transformJiraBoardProjectFilterScope(scopedResponse.results, isForRecentResults, requestDurationMs);
          } else if (currentScope === Scope.JiraProject) {
            return transformJiraProjectScope(scopedResponse.results, isForRecentResults, requestDurationMs);
          } else if (currentScope === Scope.People) {
            return transformUrsPeople(scopedResponse.results, isForRecentResults, requestDurationMs, sites.length > 0);
          }
        }))
      }), {});
    });
    const {
      recentIssuesSupplier = null
    } = config;
    this.aggregatorClient = new AggregatorClient(config);
    this.recentPeopleCache = new SimpleCache((context, sites) => this.searchForPeopleScope('', context, true, sites));
    this.recentIssuesCache = recentIssuesSupplier ? new SimpleCache(ignored => recentIssuesSupplier()) : new SimpleCache(this.recentIssuesSupplier);
    this.recentBoardsProjectsFiltersCache = new SimpleCache(async (context, sites) => {
      const results = this.searchForScope('', [], context, [Scope.JiraBoardProjectFilter], JiraSearchClient.PROJECT_BOARD_FILTER_PLAN_LIMIT, true, sites)[Scope.JiraBoardProjectFilter];
      if (!results) {
        throw new Error(`Failed to retrieve recent results for scope ${Scope.JiraBoardProjectFilter}`);
      }
      return results.promise();
    });
    this.recentPlansCache = new SimpleCache(async (context, sites) => {
      const results = this.searchForScope('', [], context, [Scope.JiraPlan], JiraSearchClient.PROJECT_BOARD_FILTER_PLAN_LIMIT, true, sites)[Scope.JiraPlan];
      if (!results) {
        throw new Error(`Failed to retrieve recent results for scope ${Scope.JiraPlan}`);
      }
      return results.promise();
    });
    this.recentProjectsCache = new SimpleCache(async (context, sites) => {
      const results = this.searchForScope('', [], context, [Scope.JiraProject], JiraSearchClient.PROJECT_RESULT_LIMIT, true, sites)[Scope.JiraProject];
      if (!results) {
        throw new Error(`Failed to retrieve recent results for scope ${Scope.JiraProject}`);
      }
      return results.promise();
    });
    this.isUserAnonymous = config.isUserAnonymous;
  }
  getRecent(cache, context, sites) {
    const {
      fromCache,
      value
    } = cache.get(context, limitToMaxSiteUsage(sites));
    return CancellablePromise.from(value.then(results => ({
      ...results,
      isCached: fromCache
    })));
  }
  getRecentBoardsProjectsFiltersPlansInterleaved(context, sites) {
    const {
      fromCache: boardsProjectsFiltersFromCache,
      value: boardsProjectsFiltersValue
    } = this.recentBoardsProjectsFiltersCache.get(context, sites);
    const {
      fromCache: plansFromCache,
      value: plansValue
    } = this.recentPlansCache.get(context, sites);
    return CancellablePromise.from(Promise.all([boardsProjectsFiltersValue, plansValue]).then(results => ({
      ...this.interleaveRecentBoardsProjectsFiltersPlans(results[0], results[1]),
      isCached: plansFromCache && boardsProjectsFiltersFromCache
    })));
  }
  getRecentIssues(context, sites) {
    return this.getRecent(this.recentIssuesCache, context, sites);
  }
  getRecentPlans(context, sites) {
    return this.getRecent(this.recentPlansCache, context, sites);
  }
  getRecentProjects(context, sites) {
    return this.getRecent(this.recentProjectsCache, context, sites);
  }
  getRecentBoardsProjectsFilters(context, sites) {
    return this.getRecent(this.recentBoardsProjectsFiltersCache, context, sites);
  }
  getRecentBoardsProjectsFiltersPlans(context, sites) {
    return this.getRecentBoardsProjectsFiltersPlansInterleaved(context, sites);
  }

  /**
   * Check the user permissions for the given list of products.
   */
  async getProductPermissions(products) {
    if (!this.permissionsDataCache) {
      this.permissionsDataCache = await this.aggregatorClient.getProductPermissions(products, NAVIGATION_V3_EXPERIENCE);
    }
    return this.permissionsDataCache;
  }
  async getAbTestData() {
    if (this.abTestDataCache) {
      return this.abTestDataCache;
    }
    const abTest = await this.aggregatorClient.getAbTestData(Scope.JiraIssue, NAVIGATION_V3_EXPERIENCE);
    this.abTestDataCache = abTest;
    return abTest;
  }
  getRecentPeople(context, sites) {
    return this.getRecent(this.recentPeopleCache, context, sites);
  }
  searchPeople(query, context, sites) {
    return CancellablePromise.from(this.searchForPeopleScope(query, context, false, sites));
  }
  searchForPeopleScope(query, context, isForRecentResults, sites, queryVersion) {
    if (this.isUserAnonymous) {
      return Promise.resolve({
        items: [],
        timings: 0,
        totalSize: 0
      });
    }
    const results = this.searchForScope(query, [], context, [Scope.People], JiraSearchClient.PEOPLE_RESULT_LIMIT, isForRecentResults, sites, queryVersion)[Scope.People];
    if (!results) {
      throw new Error(`Failed to retrieve results for scope ${Scope.People}`);
    }
    return results.promise();
  }
  searchProjects(query, context, sites) {
    const results = this.searchForScope(query, [], context, [Scope.JiraProject], JiraSearchClient.ITEM_RESULT_LIMIT, false, sites)[Scope.JiraProject];
    if (!results) {
      throw new Error(`Failed to retrieve results for scope ${Scope.JiraProject}`);
    }
    return results;
  }
}
_defineProperty(JiraSearchClient, "ITEM_RESULT_LIMIT", 50);
_defineProperty(JiraSearchClient, "PROJECT_BOARD_FILTER_LIMIT", 6);
_defineProperty(JiraSearchClient, "PROJECT_BOARD_FILTER_PLAN_LIMIT", 8);
_defineProperty(JiraSearchClient, "PROJECT_RESULT_LIMIT", 10);
_defineProperty(JiraSearchClient, "PEOPLE_RESULT_LIMIT", 10);
export function attributeTypeToContentType(attributeType) {
  switch (attributeType) {
    case AttributeType.issue:
      return ContentType.JiraIssue;
    case AttributeType.project:
      return ContentType.JiraProject;
    case AttributeType.board:
      return ContentType.JiraBoard;
    case AttributeType.filter:
      return ContentType.JiraFilter;
    case AttributeType.plan:
      return ContentType.JiraPlan;
    case AttributeType.people:
      return ContentType.JiraPeople;
    default:
      throw new UnreachableError(attributeType);
  }
}